import net from "node:net";
import type { SetSessionConfigOptionResponse } from "@agentclientprotocol/sdk";
import { normalizeOutputError } from "../../acp/error-normalization.js";
import { recordPerfDuration } from "../../perf-metrics.js";
import { textPrompt } from "../../prompt-content.js";
import type {
  AcpClientOptions,
  NonInteractivePermissionPolicy,
  PermissionMode,
  PromptInput,
  SessionResumePolicy,
} from "../../types.js";
import {
  parseQueueRequest,
  type QueueOwnerErrorMessage,
  type QueueOwnerMessage,
  type QueueRequest,
} from "./messages.js";
import { queueRequestByteLimit, queueRequestExceedsLimit } from "./request-limit.js";
import { QueueOutputBudget, QueueSocketOutput } from "./socket-output.js";

const QUEUE_SOCKET_DRAIN_TIMEOUT_MS = 1_000;

type QueueOwnerSocketLease = {
  socketPath: string;
  ownerGeneration?: number;
};

function makeQueueOwnerError(
  requestId: string,
  message: string,
  detailCode: string,
  options: {
    retryable?: boolean;
  } = {},
): QueueOwnerErrorMessage {
  return {
    type: "error",
    requestId,
    ownerGeneration: undefined,
    code: "RUNTIME",
    detailCode,
    origin: "queue",
    retryable: options.retryable,
    message,
  };
}

function makeQueueOwnerErrorFromUnknown(
  requestId: string,
  error: unknown,
  detailCode: string,
  options: {
    retryable?: boolean;
  } = {},
): QueueOwnerErrorMessage {
  const normalized = normalizeOutputError(error, {
    defaultCode: "RUNTIME",
    origin: "queue",
    detailCode,
    retryable: options.retryable,
  });

  return {
    type: "error",
    requestId,
    code: normalized.code,
    detailCode: normalized.detailCode,
    origin: normalized.origin,
    message: normalized.message,
    retryable: normalized.retryable,
    acp: normalized.acp,
  };
}

export type QueueTask = {
  requestId: string;
  message: string;
  prompt: PromptInput;
  permissionMode: PermissionMode;
  resumePolicy?: SessionResumePolicy;
  nonInteractivePermissions?: NonInteractivePermissionPolicy;
  permissionPolicy?: AcpClientOptions["permissionPolicy"];
  timeoutMs?: number;
  suppressSdkConsoleErrors?: boolean;
  promptRetries?: number;
  sessionOptions?: NonNullable<AcpClientOptions["sessionOptions"]>;
  waitForCompletion: boolean;
  reportPromptStarted?: boolean;
  enqueuedAt: number;
  send: (message: QueueOwnerMessage) => void;
  close: () => void;
};

export type QueueOwnerControlHandlers = {
  cancelPrompt: () => Promise<boolean>;
  closeSession: (timeoutMs?: number) => Promise<boolean>;
  setSessionMode: (modeId: string, timeoutMs?: number) => Promise<void>;
  setSessionModel: (
    modelId: string,
    timeoutMs?: number,
  ) => Promise<SetSessionConfigOptionResponse | undefined>;
  setSessionConfigOption: (
    configId: string,
    value: string,
    timeoutMs?: number,
  ) => Promise<SetSessionConfigOptionResponse>;
};

type SessionQueueOwnerOptions = {
  maxQueueDepth: number;
  maxRequestBytes?: number;
  onQueueDepthChanged?: (queueDepth: number) => void;
};

export class SessionQueueOwner {
  private readonly server: net.Server;
  private readonly controlHandlers: QueueOwnerControlHandlers;
  private readonly ownerGeneration?: number;
  private readonly maxQueueDepth: number;
  private readonly maxRequestBytes?: number;
  private readonly onQueueDepthChanged?: (queueDepth: number) => void;
  private readonly pending: QueueTask[] = [];
  private activeTask?: QueueTask;
  private readonly waiters: Array<(task: QueueTask | undefined) => void> = [];
  private readonly sockets = new Set<net.Socket>();
  private readonly taskSockets = new Set<net.Socket>();
  private readonly drainingSockets = new Set<net.Socket>();
  private readonly outputBudget = new QueueOutputBudget();
  private readonly outputs = new Map<net.Socket, QueueSocketOutput>();
  private closed = false;

  private constructor(
    server: net.Server,
    controlHandlers: QueueOwnerControlHandlers,
    lease: QueueOwnerSocketLease,
    options: SessionQueueOwnerOptions,
  ) {
    this.server = server;
    this.controlHandlers = controlHandlers;
    this.ownerGeneration = lease.ownerGeneration;
    this.maxQueueDepth = Math.max(1, Math.round(options.maxQueueDepth));
    this.maxRequestBytes = queueRequestByteLimit(options.maxRequestBytes?.toString());
    this.onQueueDepthChanged = options.onQueueDepthChanged;
  }

  static async start(
    lease: QueueOwnerSocketLease,
    controlHandlers: QueueOwnerControlHandlers,
    options: SessionQueueOwnerOptions = {
      maxQueueDepth: 16,
    },
  ): Promise<SessionQueueOwner> {
    const ownerRef: { current: SessionQueueOwner | undefined } = { current: undefined };
    const server = net.createServer((socket) => {
      ownerRef.current?.handleConnection(socket);
    });
    ownerRef.current = new SessionQueueOwner(server, controlHandlers, lease, options);

    await new Promise<void>((resolve, reject) => {
      const onListening = () => {
        server.off("error", onError);
        resolve();
      };
      const onError = (error: Error) => {
        server.off("listening", onListening);
        reject(error);
      };

      server.once("listening", onListening);
      server.once("error", onError);
      server.listen(lease.socketPath);
    });

    return ownerRef.current;
  }

  beginShutdown(): void {
    if (this.closed) {
      return;
    }

    this.closed = true;
    for (const waiter of this.waiters.splice(0)) {
      waiter(undefined);
    }

    for (const task of this.pending.splice(0)) {
      if (task.waitForCompletion) {
        task.send(
          makeQueueOwnerError(
            task.requestId,
            "Queue owner shutting down before prompt execution",
            "QUEUE_OWNER_SHUTTING_DOWN",
            {
              retryable: true,
            },
          ),
        );
      }
      task.close();
    }
    for (const socket of this.sockets) {
      if (!this.taskSockets.has(socket) && !this.drainingSockets.has(socket)) {
        socket.destroy();
      }
    }
    this.emitQueueDepth();
  }

  async close(): Promise<void> {
    this.beginShutdown();

    await new Promise<void>((resolve) => {
      const forceCloseTimer = setTimeout(() => {
        for (const socket of this.sockets) {
          socket.destroy();
        }
      }, QUEUE_SOCKET_DRAIN_TIMEOUT_MS);
      forceCloseTimer.unref();
      this.server.close(() => {
        clearTimeout(forceCloseTimer);
        resolve();
      });
    });
  }

  async nextTask(timeoutMs?: number): Promise<QueueTask | undefined> {
    if (this.pending.length > 0) {
      const task = this.pending.shift();
      this.emitQueueDepth();
      if (task) {
        this.activeTask = task;
        recordPerfDuration("queue.owner.wait_ms", Date.now() - task.enqueuedAt);
      }
      return task;
    }
    if (this.closed) {
      return undefined;
    }

    return await new Promise<QueueTask | undefined>((resolve) => {
      const shouldTimeout = timeoutMs != null;
      const timer =
        shouldTimeout &&
        setTimeout(
          () => {
            const index = this.waiters.indexOf(waiter);
            if (index >= 0) {
              this.waiters.splice(index, 1);
            }
            resolve(undefined);
          },
          Math.max(0, timeoutMs),
        );

      const waiter = (task: QueueTask | undefined) => {
        if (timer) {
          clearTimeout(timer);
        }
        resolve(task);
      };

      this.waiters.push(waiter);
    });
  }

  queueDepth(): number {
    return this.pending.length;
  }

  completeTask(task: QueueTask): void {
    if (this.activeTask === task) {
      this.activeTask = undefined;
    }
  }

  private async cancelPrompt(targetRequestId?: string): Promise<boolean> {
    if (targetRequestId === undefined) {
      return await this.controlHandlers.cancelPrompt();
    }
    const index = this.pending.findIndex((task) => task.requestId === targetRequestId);
    if (index >= 0) {
      const [task] = this.pending.splice(index, 1);
      task.send(
        makeQueueOwnerError(
          task.requestId,
          "Prompt cancelled before execution",
          "QUEUE_REQUEST_CANCELLED",
          {
            retryable: false,
          },
        ),
      );
      task.close();
      this.emitQueueDepth();
      return true;
    }
    return this.activeTask?.requestId === targetRequestId
      ? await this.controlHandlers.cancelPrompt()
      : false;
  }

  private emitQueueDepth(): void {
    this.onQueueDepthChanged?.(this.pending.length);
  }

  private enqueue(task: QueueTask): boolean {
    if (this.closed) {
      task.send(
        makeQueueOwnerError(
          task.requestId,
          "Queue owner is shutting down",
          "QUEUE_OWNER_SHUTTING_DOWN",
          {
            retryable: true,
          },
        ),
      );
      task.close();
      return false;
    }

    if (
      this.activeTask?.requestId === task.requestId ||
      this.pending.some((queued) => queued.requestId === task.requestId)
    ) {
      task.send(
        makeQueueOwnerError(
          task.requestId,
          "Request is already queued or active",
          "QUEUE_REQUEST_DUPLICATE",
          {
            retryable: false,
          },
        ),
      );
      task.close();
      return false;
    }

    const waiter = this.waiters.shift();
    if (waiter) {
      this.activeTask = task;
      waiter(task);
      return true;
    }

    if (this.pending.length >= this.maxQueueDepth) {
      task.send({
        ...makeQueueOwnerError(
          task.requestId,
          `Queue owner is overloaded (${this.pending.length}/${this.maxQueueDepth} queued)`,
          "QUEUE_OWNER_OVERLOADED",
          {
            retryable: true,
          },
        ),
        ownerGeneration: this.ownerGeneration,
      });
      task.close();
      return false;
    }

    this.pending.push(task);
    this.emitQueueDepth();
    return true;
  }

  private handleControlRequest<TMessage extends QueueOwnerMessage>(options: {
    socket: net.Socket;
    requestId: string;
    run: () => Promise<TMessage>;
  }): void {
    this.taskSockets.add(options.socket);
    this.writeQueueMessage(options.socket, {
      type: "accepted",
      requestId: options.requestId,
      ownerGeneration: this.ownerGeneration,
    });

    void options
      .run()
      .then((message) => {
        this.writeQueueMessage(options.socket, {
          ...message,
          ownerGeneration: this.ownerGeneration,
        });
      })
      .catch((error) => {
        this.writeQueueMessage(options.socket, {
          ...makeQueueOwnerErrorFromUnknown(
            options.requestId,
            error,
            "QUEUE_CONTROL_REQUEST_FAILED",
          ),
          ownerGeneration: this.ownerGeneration,
        });
      })
      .finally(() => {
        this.endSocket(options.socket);
      });
  }

  private writeQueueMessage(socket: net.Socket, message: QueueOwnerMessage): void {
    this.outputs.get(socket)?.send(message);
  }

  private endSocket(socket: net.Socket): void {
    if (socket.destroyed || this.drainingSockets.has(socket)) {
      return;
    }
    this.taskSockets.delete(socket);
    this.drainingSockets.add(socket);
    this.outputs.get(socket)?.end();
  }

  private failRequest(
    socket: net.Socket,
    requestId: string,
    message: string,
    detailCode: string,
  ): void {
    this.writeQueueMessage(socket, {
      ...makeQueueOwnerError(requestId, message, detailCode, {
        retryable: false,
      }),
      ownerGeneration: this.ownerGeneration,
    });
    this.endSocket(socket);
  }

  private parseRequestLine(socket: net.Socket, line: string): QueueRequest | undefined {
    let parsed: unknown;
    try {
      parsed = JSON.parse(line);
    } catch {
      this.failRequest(
        socket,
        "unknown",
        "Invalid queue request payload",
        "QUEUE_REQUEST_PAYLOAD_INVALID_JSON",
      );
      return undefined;
    }
    const request = parseQueueRequest(parsed);
    if (!request) {
      this.failRequest(socket, "unknown", "Invalid queue request", "QUEUE_REQUEST_INVALID");
    }
    return request ?? undefined;
  }

  private rejectStaleOwnerGeneration(socket: net.Socket, request: QueueRequest): boolean {
    if (
      request.ownerGeneration === undefined ||
      this.ownerGeneration === undefined ||
      request.ownerGeneration === this.ownerGeneration
    ) {
      return false;
    }
    this.failRequest(
      socket,
      request.requestId,
      "Queue request targeted a stale queue owner generation",
      "QUEUE_OWNER_GENERATION_MISMATCH",
    );
    return true;
  }

  private rejectClosedOwner(socket: net.Socket, request: QueueRequest): boolean {
    if (!this.closed) {
      return false;
    }
    this.writeQueueMessage(socket, {
      ...makeQueueOwnerError(
        request.requestId,
        "Queue owner is shutting down",
        "QUEUE_OWNER_CLOSED",
        {
          retryable: true,
        },
      ),
      ownerGeneration: this.ownerGeneration,
    });
    this.endSocket(socket);
    return true;
  }

  private handleControlQueueRequest(socket: net.Socket, request: QueueRequest): boolean {
    if (request.type === "cancel_prompt") {
      this.handleControlRequest({
        socket,
        requestId: request.requestId,
        run: async () => ({
          type: "cancel_result",
          requestId: request.requestId,
          cancelled: await this.cancelPrompt(request.targetRequestId),
        }),
      });
      return true;
    }
    if (request.type === "close_session") {
      this.handleControlRequest({
        socket,
        requestId: request.requestId,
        run: async () => ({
          type: "close_session_result",
          requestId: request.requestId,
          closed: await this.controlHandlers.closeSession(request.timeoutMs),
        }),
      });
      return true;
    }
    return this.handleSessionControlQueueRequest(socket, request);
  }

  private handleSessionControlQueueRequest(socket: net.Socket, request: QueueRequest): boolean {
    if (request.type === "set_mode") {
      this.handleControlRequest({
        socket,
        requestId: request.requestId,
        run: async () => {
          await this.controlHandlers.setSessionMode(request.modeId, request.timeoutMs);
          return {
            type: "set_mode_result",
            requestId: request.requestId,
            modeId: request.modeId,
          };
        },
      });
      return true;
    }
    if (request.type === "set_model") {
      this.handleControlRequest({
        socket,
        requestId: request.requestId,
        run: async () => {
          const response = await this.controlHandlers.setSessionModel(
            request.modelId,
            request.timeoutMs,
          );
          return {
            type: "set_model_result",
            requestId: request.requestId,
            modelId: request.modelId,
            response,
          };
        },
      });
      return true;
    }
    if (request.type === "set_config_option") {
      this.handleControlRequest({
        socket,
        requestId: request.requestId,
        run: async () => ({
          type: "set_config_option_result",
          requestId: request.requestId,
          response: await this.controlHandlers.setSessionConfigOption(
            request.configId,
            request.value,
            request.timeoutMs,
          ),
        }),
      });
      return true;
    }
    return false;
  }

  private enqueuePromptRequest(
    socket: net.Socket,
    request: Extract<QueueRequest, { type: "submit_prompt" }>,
  ): void {
    this.taskSockets.add(socket);
    const task: QueueTask = {
      requestId: request.requestId,
      message: request.message,
      prompt: request.prompt ?? textPrompt(request.message),
      permissionMode: request.permissionMode,
      resumePolicy: request.resumePolicy,
      nonInteractivePermissions: request.nonInteractivePermissions,
      permissionPolicy: request.permissionPolicy,
      timeoutMs: request.timeoutMs,
      suppressSdkConsoleErrors: request.suppressSdkConsoleErrors,
      promptRetries: request.promptRetries,
      sessionOptions: request.sessionOptions,
      waitForCompletion: request.waitForCompletion,
      reportPromptStarted: request.reportPromptStarted,
      enqueuedAt: Date.now(),
      send: (message) => {
        this.writeQueueMessage(socket, {
          ...message,
          ownerGeneration: this.ownerGeneration,
        });
      },
      close: () => {
        this.endSocket(socket);
      },
    };

    const enqueued = this.enqueue(task);
    if (!enqueued) {
      return;
    }

    this.writeQueueMessage(socket, {
      type: "accepted",
      requestId: request.requestId,
      ownerGeneration: this.ownerGeneration,
    });
    if (!request.waitForCompletion) {
      task.close();
    }
  }

  private handleConnection(socket: net.Socket): void {
    this.sockets.add(socket);
    this.outputs.set(socket, new QueueSocketOutput(socket, this.outputBudget));
    socket.setEncoding("utf8");
    socket.on("timeout", () => socket.destroy());
    socket.once("close", () => {
      this.outputs.delete(socket);
      this.sockets.delete(socket);
      this.taskSockets.delete(socket);
      this.drainingSockets.delete(socket);
    });

    let buffer = "";
    let handled = false;

    const processLine = (line: string): void => {
      if (handled) {
        return;
      }
      handled = true;

      const request = this.parseRequestLine(socket, line);
      if (
        !request ||
        this.rejectClosedOwner(socket, request) ||
        this.rejectStaleOwnerGeneration(socket, request)
      ) {
        return;
      }
      if (this.handleControlQueueRequest(socket, request)) {
        return;
      }
      if (request.type !== "submit_prompt") {
        this.failRequest(
          socket,
          request.requestId,
          "Invalid queue request",
          "QUEUE_REQUEST_INVALID",
        );
        return;
      }
      this.enqueuePromptRequest(socket, request);
    };

    socket.on("data", (chunk: string) => {
      buffer += chunk;

      let index = buffer.indexOf("\n");
      while (index >= 0) {
        const rawLine = buffer.slice(0, index);
        if (this.rejectOversizedRequest(socket, rawLine)) {
          return;
        }
        const line = rawLine.trim();
        buffer = buffer.slice(index + 1);

        if (line.length > 0) {
          processLine(line);
        }

        index = buffer.indexOf("\n");
      }
      this.rejectOversizedRequest(socket, buffer);
    });

    socket.on("error", () => {
      // no-op: queue processing continues even if client disconnects
    });
  }

  private rejectOversizedRequest(socket: net.Socket, line: string): boolean {
    if (!queueRequestExceedsLimit(line, this.maxRequestBytes)) {
      return false;
    }
    socket.destroy();
    return true;
  }
}
